import React from "react";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { eventsApi } from "../../api";
import type { EventAiInsights, EventFinanceOverview, EventListItem, Venue } from "../../types";
import { formatDisplayDate, formatINRCompact } from "../../utils/format";
import { appNav } from "../../routes/appNavigation";
import { workspaceNav, type EventWorkspaceBackState } from "../../routes/workspaceNavigation";
import type { WorkspaceId } from "../../utils/workspaceStorage";
import "./FinanceLayout.css";

type Props = {
  event: EventListItem;
  workspace: WorkspaceId;
  token?: string | null;
  venue?: Venue;
  expanded: boolean;
  onToggle: () => void;
};

type ChannelRow = {
  channel: string;
  mix: string;
  ticket: string;
  cashLands: string;
  ticketValue: number | null;
  lowest?: boolean;
};

function initialsFromName(name: string): string {
  return (
    name
      .split(/\s+/)
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Record<string, unknown>) : null;
}

function num(value: unknown): number | null {
  if (typeof value === "number" && Number.isFinite(value)) return value;
  if (typeof value === "string" && value.trim() && Number.isFinite(Number(value))) return Number(value);
  return null;
}

function str(value: unknown): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (typeof value === "number" && Number.isFinite(value)) return String(value);
  return "—";
}

function pick(obj: Record<string, unknown>, keys: string[]): unknown {
  const normalized = Object.keys(obj).reduce<Record<string, unknown>>((acc, key) => {
    acc[key.toLowerCase().replace(/[_\s-]/g, "")] = obj[key];
    return acc;
  }, {});
  for (const key of keys) {
    const found = normalized[key.toLowerCase().replace(/[_\s-]/g, "")];
    if (found != null) return found;
  }
  return undefined;
}

function formatMix(value: unknown): string {
  const n = num(value);
  if (n == null) return str(value);
  if (n <= 1) return `${Math.round(n * 100)}%`;
  if (n <= 100) return `${Math.round(n)}%`;
  return `${Math.round(n)}`;
}

function formatTicket(value: unknown): string {
  const n = num(value);
  return n == null ? str(value) : n.toLocaleString("en-IN");
}

function rowFromUnknown(item: unknown, fallbackName: string): ChannelRow | null {
  const obj = asRecord(item);
  if (!obj) return null;
  const channel = str(pick(obj, ["channel", "name", "partner", "source"]) || fallbackName);
  if (channel === "—") return null;
  const ticketRaw = pick(obj, ["ticket", "ticket_price", "avg_ticket", "price", "net_ticket"]);
  return {
    channel,
    mix: formatMix(pick(obj, ["mix", "share", "pct", "percentage", "mix_pct"])),
    ticket: formatTicket(ticketRaw),
    cashLands: str(pick(obj, ["cash_lands", "cashLands", "settlement", "t_plus", "payout", "lands"])),
    ticketValue: num(ticketRaw),
  };
}

function parseChannelEconomics(raw: EventFinanceOverview["channel_economics"]): ChannelRow[] {
  if (!raw) return [];
  const list = Array.isArray(raw)
    ? raw
    : Array.isArray(raw.channels)
      ? raw.channels
      : Array.isArray(raw.items)
        ? raw.items
        : Array.isArray(raw.partners)
          ? raw.partners
          : null;

  const rows = (
    list
      ? list.map((item, index) => rowFromUnknown(item, `Channel ${index + 1}`))
      : Object.entries(raw).map(([key, value]) => rowFromUnknown(value, key))
  ).filter((row): row is ChannelRow => Boolean(row));

  const priced = rows.filter((row) => row.ticketValue != null);
  if (priced.length < 2) return rows;
  const lowest = Math.min(...priced.map((row) => row.ticketValue as number));
  return rows.map((row) => (row.ticketValue === lowest ? { ...row, lowest: true } : row));
}

function venueSubtitle(event: EventListItem, venue?: Venue): string {
  const area = venue?.landmark || venue?.district || venue?.venue_name || null;
  const parts = [
    event.city || venue?.city || null,
    area && area !== event.city ? area : venue?.venue_name && venue.venue_name !== area ? venue.venue_name : null,
    `Capacity ${event.venue_capacity.toLocaleString("en-IN")}`,
  ].filter(Boolean);
  return parts.join(" · ");
}

function formatPace(value: number | null | undefined): string {
  if (value == null || !Number.isFinite(value)) return "—";
  if (Math.abs(value) >= 10) return Math.round(value).toLocaleString("en-IN");
  return value.toFixed(1).replace(/\.0$/, "");
}

function insightsCopy(insights: EventAiInsights): string {
  if (insights.mode === "pre_event") {
    const bits: string[] = [];
    if (insights.demand && insights.demand !== "unknown") bits.push(`Demand ${insights.demand}`);
    if (insights.health_score != null) bits.push(`Health ${insights.health_score}`);
    if (insights.roi != null) bits.push(`ROI ${insights.roi}%`);
    if (insights.sellout_eta_days != null) bits.push(`Sellout ~${insights.sellout_eta_days}d`);
    return bits.join(" · ") || "Gathering sales data for this show.";
  }
  const invoices = insights.upcoming_invoices ?? [];
  if (invoices.length) {
    const total = invoices.reduce((sum, invoice) => sum + invoice.amount, 0);
    return `${invoices.length} outstanding invoices totalling ${formatINRCompact(total)}. Watch payout timing.`;
  }
  return "No outstanding invoices for this event.";
}

function clampPct(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.max(0, Math.min(100, value));
}

export const EventFinanceCard: React.FC<Props> = ({ event, workspace, token, venue, expanded, onToggle }) => {
  const navigate = useNavigate();

  const openPnl = (e: React.MouseEvent) => {
    e.stopPropagation();
    const backState: EventWorkspaceBackState = { backTo: appNav.financeEvents(), backLabel: "Finance" };
    navigate(workspaceNav.eventPnl(workspace, event.id), { state: backState });
  };

  const overviewQuery = useQuery<EventFinanceOverview>({
    queryKey: ["event-finance-overview", event.id],
    queryFn: () => eventsApi.financeOverview(event.id, token!),
    enabled: expanded && Boolean(token),
  });

  const insightsQuery = useQuery<EventAiInsights>({
    queryKey: ["event-ai-insights", event.id],
    queryFn: () => eventsApi.aiInsights(event.id, token!),
    enabled: expanded && Boolean(token),
  });

  const overview = overviewQuery.data;
  const insights = insightsQuery.data;
  const channels = parseChannelEconomics(overview?.channel_economics ?? null);
  const breakEvenPct = overview
    ? clampPct(
        overview.break_even.break_even_tickets
          ? (overview.break_even.tickets_sold / overview.break_even.break_even_tickets) * 100
          : 0,
      )
    : 0;
  const pacePct = overview?.pace.tickets_required_per_day
    ? clampPct(((overview.pace.tickets_sold_per_day ?? 0) / overview.pace.tickets_required_per_day) * 100)
    : 0;

  return (
    <article className={`eventos-card finance-event-card${expanded ? " is-open" : ""}`}>
      <div className="finance-event-card__head">
        <button
          type="button"
          className="finance-event-card__head-toggle"
          onClick={onToggle}
          onDoubleClick={() => navigate(workspaceNav.dashboard(workspace, event.id))}
          aria-expanded={expanded}
          aria-controls={`event-finance-${event.id}`}
        >
          {venue?.photo_url ? (
            <img className="finance-event-card__thumb" src={venue.photo_url} alt="" />
          ) : (
            <span className="finance-event-card__thumb" aria-hidden>
              {initialsFromName(event.name)}
            </span>
          )}
          <span className="finance-event-card__identity">
            <span className="finance-event-card__title">{event.name}</span>
            <span className="finance-event-card__meta">{venueSubtitle(event, venue)}</span>
          </span>
        </button>
        <span className="finance-event-card__aside">
          <span className="finance-event-card__date">{formatDisplayDate(event.event_date)}</span>
          <button type="button" className="finance-event-card__pnl-btn" onClick={openPnl}>
            Open in P&amp;L
          </button>
          <button
            type="button"
            className="finance-event-card__chevron-btn"
            onClick={onToggle}
            aria-expanded={expanded}
            aria-controls={`event-finance-${event.id}`}
            aria-label={expanded ? "Collapse event finance details" : "Expand event finance details"}
          >
            <ChevronDown className="finance-event-card__chevron" size={16} strokeWidth={2.2} aria-hidden />
          </button>
        </span>
      </div>

      <div id={`event-finance-${event.id}`} className="finance-event-card__collapse">
        <div className="finance-event-card__body">
          <section className="finance-event-card__insights">
            <div className="finance-event-card__stat-label">AI Insights</div>
            {insightsQuery.isPending ? (
              <p className="finance-event-card__insights-copy">Loading insights…</p>
            ) : insights ? (
              <p className="finance-event-card__insights-copy">{insightsCopy(insights)}</p>
            ) : (
              <p className="finance-event-card__insights-copy">No insights yet for this show.</p>
            )}
          </section>

          {overviewQuery.isPending ? (
            <div className="finance-event-card__loading">Loading finance overview…</div>
          ) : overview ? (
            <div className="finance-event-card__grid">
              <div className="finance-event-card__stat">
                <div className="finance-event-card__stat-label">Break-Even</div>
                <div className="finance-event-card__stat-value">
                  {overview.break_even.tickets_to_go.toLocaleString("en-IN")}
                </div>
                <div className="finance-event-card__stat-sub">Tickets to Go</div>
                <div className="finance-event-card__progress" aria-hidden>
                  <div className="finance-event-card__progress-fill" style={{ width: `${breakEvenPct}%` }} />
                </div>
              </div>

              <div className="finance-event-card__stat">
                <div className="finance-event-card__stat-label">Pace</div>
                <div className="finance-event-card__stat-value">
                  {formatPace(overview.pace.tickets_sold_per_day)}
                  <span className="finance-event-card__stat-unit">/day</span>
                </div>
                <div className="finance-event-card__stat-sub">
                  Req {formatPace(overview.pace.tickets_required_per_day)}
                </div>
                <div className="finance-event-card__progress" aria-hidden>
                  <div
                    className={`finance-event-card__progress-fill${overview.pace.on_pace === false ? " is-down" : ""}`}
                    style={{ width: `${pacePct}%` }}
                  />
                </div>
              </div>

              <div className="finance-event-card__stat finance-event-card__stat--table">
                <div className="finance-event-card__stat-label">Channel Economics</div>
                {channels.length === 0 ? (
                  <p className="finance-event-card__empty">No channel mix yet.</p>
                ) : (
                  <table className="finance-event-card__channels">
                    <thead>
                      <tr>
                        <th>Channel</th>
                        <th>Mix</th>
                        <th>Ticket</th>
                        <th>Cash Lands</th>
                      </tr>
                    </thead>
                    <tbody>
                      {channels.map((row) => (
                        <tr key={row.channel}>
                          <td>{row.channel}</td>
                          <td>{row.mix}</td>
                          <td>
                            {row.ticket}
                            {row.lowest ? <span className="finance-event-card__lowest"> lowest</span> : null}
                          </td>
                          <td>{row.cashLands}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>

              <div className="finance-event-card__stat finance-event-card__stat--pnl">
                <div className="finance-event-card__pnl">
                  <div className="finance-event-card__pnl-row">
                    <span>Revenue</span>
                    <strong>{formatINRCompact(overview.revenue)}</strong>
                  </div>
                  <div className={`finance-event-card__pnl-row${overview.profit >= 0 ? " is-up" : " is-down"}`}>
                    <span>Profit</span>
                    <strong>{formatINRCompact(overview.profit)}</strong>
                  </div>
                  <div className="finance-event-card__pnl-row">
                    <span>Expense</span>
                    <strong>{formatINRCompact(overview.expense)}</strong>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="finance-event-card__loading">Unable to load finance overview.</div>
          )}
        </div>
      </div>
    </article>
  );
};

export default EventFinanceCard;
